import { useEffect } from 'react';

interface PageMetaProps {
  title: string;
  description: string;
  url?: string;
  type?: string;
  image?: string;
}

/**
 * Lightweight per-page SEO component.
 * Sets document.title and upserts <meta> tags in <head> via useEffect.
 * Restores document.title and removes newly created meta tags on unmount.
 * Note: pre-existing meta tags that were updated are not restored to their
 * previous values — only tags created by this component are removed.
 */
export function PageMeta({ title, description, url, type = 'website', image }: PageMetaProps) {
  useEffect(() => {
    const prev = document.title;
    document.title = title;

    const tags: Array<{ property?: string; name?: string; content: string }> = [
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: type },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
    ];

    if (url) {
      tags.push({ property: 'og:url', content: url });
    }
    if (image) {
      tags.push({ property: 'og:image', content: image });
      tags.push({ name: 'twitter:image', content: image });
    }

    const created: HTMLMetaElement[] = [];

    for (const tag of tags) {
      const selector = tag.property
        ? `meta[property="${tag.property}"]`
        : `meta[name="${tag.name}"]`;
      let el = document.head.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        if (tag.property) el.setAttribute('property', tag.property);
        if (tag.name) el.setAttribute('name', tag.name);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute('content', tag.content);
    }

    return () => {
      document.title = prev;
      for (const el of created) {
        el.remove();
      }
    };
  }, [title, description, url, type, image]);

  return null;
}
